// 관리자 회원관리 화면.
// 작업계획서 5.5: 회원 목록/검색, 등급/적립금/쿠폰 조정, 노쇼/이용제한.
//  - 좌측 목록(검색) + 선택 회원 상세/조정 패널.
//  - 상단에 전체 회원 일괄 쿠폰 지급 진입(개선 3).
//  - 회원 상세에는 누적 구매(전체)와 등급 산정 실적(최근 N개월)을 구분해 표시(개선 2),
//    하단에는 해당 회원의 주문 내역 리스트(클릭 시 상세 모달)를 보여준다(개선 5).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  COUPON_TYPE,
  ORDER_STATUS_ADMIN_LABEL_KO,
  PAYMENT_METHOD_TYPE_LABEL_KO,
  PAYMENT_STATUS,
  PAYMENT_STATUS_LABEL_KO,
  formatCartLineLabel,
  formatDesiredPickup,
  formatPoints,
  type AdminGrantCouponInput,
  type AdminOrderDto,
  type AdminUserDto,
  type CouponType,
  type OrderStatus,
  type PaymentStatus,
} from "@oo/shared";
import { Button, useConfirm } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import { ApiError } from "@/api/client.ts";
import {
  adjustPoints,
  getMember,
  grantCoupon,
  grantCouponToAll,
  listMemberOrders,
  listMembers,
  setRestricted,
} from "@/features/members/membersApi.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";

// 회원 검색/관리 화면 (적립금·쿠폰 지급, 이용제한).
export default function MembersPage() {
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 전체 회원 일괄 쿠폰 지급 모달 열림 여부.
  const [bulkOpen, setBulkOpen] = useState(false);

  const listQuery = useQuery({
    queryKey: ["admin-members", submitted],
    queryFn: () => listMembers(submitted),
  });

  return (
    <div className="mx-auto flex min-w-0 w-full max-w-5xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="회원 관리"
        description="회원을 검색해 등급/적립금/쿠폰을 지급하고 이용을 제한합니다."
        actions={
          <Button type="button" variant="secondary" onClick={() => setBulkOpen(true)}>
            전체 회원 쿠폰 지급
          </Button>
        }
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(q);
        }}
        className="flex gap-2"
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="휴대폰 번호 또는 이름 검색"
          className="min-h-touch min-w-0 flex-1 rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-500"
        />
        <Button type="submit" variant="secondary">
          검색
        </Button>
      </form>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 목록 */}
        <section className="flex flex-col gap-2">
          {listQuery.isLoading ? (
            <p className="text-sm text-ink-600">불러오는 중...</p>
          ) : (listQuery.data?.length ?? 0) === 0 ? (
            <p className="rounded-lg border border-dashed border-ink-300 p-6 text-center text-sm text-ink-600">
              회원이 없습니다.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {listQuery.data?.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(u.id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border p-3 text-left ${
                      selectedId === u.id
                        ? "border-brand-600 bg-ink-50"
                        : "border-ink-200 bg-white hover:bg-ink-50"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {u.displayName ?? "이름 미등록"}
                        {u.isRestricted ? (
                          <span className="ml-1 rounded bg-brand-50 px-1 text-[10px] text-brand-700">
                            제한
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-ink-600 tabular-nums">{formatPhone(u.phone)}</p>
                    </div>
                    <div className="shrink-0 text-right text-xs">
                      <p className="font-semibold">{u.tierName}</p>
                      <p className="tabular-nums text-ink-600">{formatPoints(u.pointsBalance)}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 상세/조정 */}
        <section className="min-w-0">
          {selectedId ? (
            <MemberDetail id={selectedId} />
          ) : (
            <p className="rounded-lg border border-dashed border-ink-300 p-8 text-center text-sm text-ink-600">
              회원을 선택하면 상세/조정이 표시됩니다.
            </p>
          )}
        </section>
      </div>

      {/* 전체 회원 일괄 쿠폰 지급 모달 */}
      {bulkOpen ? <BulkCouponModal onClose={() => setBulkOpen(false)} /> : null}
    </div>
  );
}

function MemberDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const memberQuery = useQuery({ queryKey: ["admin-member", id], queryFn: () => getMember(id) });
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["admin-member", id] });
    void qc.invalidateQueries({ queryKey: ["admin-members"] });
  };

  // 적립금 지급(차감 포함).
  const [pointAmount, setPointAmount] = useState<number>(0);
  const [pointReason, setPointReason] = useState("");
  const pointsMut = useMutation({
    mutationFn: () => adjustPoints(id, pointAmount, pointReason.trim()),
    onSuccess: (res) => {
      setMsg(`적립금 ${res.applied > 0 ? "+" : ""}${res.applied.toLocaleString()}P 반영`);
      setPointAmount(0);
      setPointReason("");
      refresh();
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "적립금 지급 실패"),
  });

  // 쿠폰 지급 (CouponGrantForm 이 입력을 모아 spec 으로 전달).
  const couponMut = useMutation({
    mutationFn: (spec: AdminGrantCouponInput) => grantCoupon(id, spec),
    onSuccess: (_res, spec) => {
      setMsg(`쿠폰 ${spec.count}장 지급`);
      refresh();
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "쿠폰 지급 실패"),
  });

  const restrictMut = useMutation({
    mutationFn: (restricted: boolean) => setRestricted(id, restricted),
    onSuccess: () => {
      setMsg("이용 제한 상태를 변경했습니다.");
      refresh();
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "변경 실패"),
  });

  if (memberQuery.isLoading || !memberQuery.data) {
    return <div className="h-40 animate-pulse rounded-lg bg-ink-100" />;
  }
  const u: AdminUserDto = memberQuery.data;

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-3 rounded-lg border border-ink-200 bg-white p-4">
      <div>
        <p className="text-base font-bold">{u.displayName ?? "이름 미등록"}</p>
        <p className="text-sm text-ink-600 tabular-nums">{formatPhone(u.phone)}</p>
      </div>
      <div className="grid grid-cols-1 gap-2 rounded-md bg-ink-50 p-3 text-sm sm:grid-cols-2">
        <Stat label="등급" value={u.tierName} />
        <Stat label="적립금" value={formatPoints(u.pointsBalance)} />
        {/* 누적 구매(전체)는 기간 제한 없는 평생 누적액. */}
        <Stat label="누적 구매(전체)" value={`${u.totalSpentKrw.toLocaleString()}원`} />
        {/* 등급 산정 실적은 등급을 정하는 최근 N개월 윈도우 합계(매 산정 주기마다 갱신). */}
        <Stat
          label={`등급 산정 실적${u.tierWindowMonths ? `(최근 ${u.tierWindowMonths}개월)` : ""}`}
          value={u.tierSpentKrw != null ? `${u.tierSpentKrw.toLocaleString()}원` : "-"}
        />
        <Stat label="노쇼" value={`${u.noShowCount}회`} />
        {/* 마케팅 수신 동의(가입 시 선택). 전체 공지/푸시는 동의자에게만 발송된다. */}
        <Stat
          label="알림 수신 동의"
          value={`푸시 ${u.marketingPush ? "동의" : "미동의"} · SMS ${u.marketingSms ? "동의" : "미동의"}`}
        />
      </div>

      {msg ? <p className="text-xs text-ink-600">{msg}</p> : null}

      {/* 적립금 지급 */}
      <fieldset className="min-w-0 max-w-full rounded-md border border-ink-200 p-3">
        <legend className="px-1 text-xs font-semibold text-ink-600">적립금 지급</legend>
        <div className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] items-center gap-2 sm:flex sm:flex-wrap">
          <input
            type="number"
            value={pointAmount}
            onChange={(e) => setPointAmount(Number.parseInt(e.target.value, 10) || 0)}
            placeholder="금액(+/-)"
            className="h-9 w-28 rounded-md border border-ink-300 px-2 text-sm tabular-nums"
          />
          <input
            type="text"
            value={pointReason}
            onChange={(e) => setPointReason(e.target.value)}
            maxLength={100}
            placeholder="사유"
            className="h-9 min-w-0 flex-1 rounded-md border border-ink-300 px-2 text-sm"
          />
          <button
            type="button"
            disabled={pointsMut.isPending || pointAmount === 0 || !pointReason.trim()}
            onClick={() => pointsMut.mutate()}
            className="col-span-2 h-9 justify-self-end rounded-md bg-brand-600 px-3 text-xs font-semibold text-white disabled:opacity-50 sm:col-span-1"
          >
            반영
          </button>
        </div>
      </fieldset>

      {/* 쿠폰 지급 */}
      <fieldset className="min-w-0 max-w-full rounded-md border border-ink-200 p-3">
        <legend className="px-1 text-xs font-semibold text-ink-600">쿠폰 지급</legend>
        <CouponGrantForm
          pending={couponMut.isPending}
          submitLabel="지급"
          onSubmit={(spec) => couponMut.mutate(spec)}
        />
      </fieldset>

      {/* 이용 제한 */}
      <button
        type="button"
        disabled={restrictMut.isPending}
        onClick={async () => {
          const ok = await confirm({
            title: u.isRestricted ? "이용 제한 해제" : "이용 제한",
            description: u.isRestricted
              ? "이 회원의 이용 제한을 해제할까요?"
              : "이 회원의 이용을 제한할까요?",
            confirmLabel: u.isRestricted ? "해제" : "제한",
            tone: u.isRestricted ? "brand" : "danger",
          });
          if (ok) {
            restrictMut.mutate(!u.isRestricted);
          }
        }}
        className={`h-10 rounded-md border text-sm font-semibold ${
          u.isRestricted
            ? "border-ink-400 text-ink-700 hover:bg-ink-100"
            : "border-brand-300 text-brand-700 hover:bg-brand-50"
        } disabled:opacity-50`}
      >
        {u.isRestricted ? "이용 제한 해제" : "이용 제한"}
      </button>

      {/* 주문 내역 (개선 5) */}
      <MemberOrdersSection id={id} />
    </div>
  );
}

// 쿠폰 지급 입력 폼 (회원 1명 / 전체 일괄 공용).
//  - 1줄: 유형/혜택값/이름(넓게)/유효기간/장수. 2줄: 사용 조건(최소주문/최대할인).
//  - '지급' 버튼은 사용 조건 입력칸 하단에 둔다(개선 4).
function CouponGrantForm({
  pending,
  submitLabel,
  onSubmit,
}: {
  pending: boolean;
  submitLabel: string;
  onSubmit: (spec: AdminGrantCouponInput) => void;
}) {
  const [cType, setCType] = useState<CouponType>(COUPON_TYPE.PERCENT);
  const [cValue, setCValue] = useState<number>(5);
  const [cName, setCName] = useState("");
  const [cDays, setCDays] = useState<number>(30);
  const [cCount, setCCount] = useState<number>(1);
  // 사용 조건(선택). 빈 칸(0)이면 제한 없음으로 보낸다(null). 최대 할인은 정률 쿠폰에만.
  const [cMinOrder, setCMinOrder] = useState<number>(0);
  const [cMaxDiscount, setCMaxDiscount] = useState<number>(0);
  // 사용 조건: 주문 방식 제한(""=전체).
  const [cOrderType, setCOrderType] = useState<"" | "pickup" | "delivery">("");

  const submit = () => {
    onSubmit({
      type: cType,
      value: cType === COUPON_TYPE.FREE_DELIVERY ? 0 : cValue,
      name: cName.trim(),
      validDays: cDays,
      count: cCount,
      minOrderKrw: cMinOrder > 0 ? cMinOrder : null,
      maxDiscountKrw: cType === COUPON_TYPE.PERCENT && cMaxDiscount > 0 ? cMaxDiscount : null,
      applicableOrderType: cOrderType === "" ? null : cOrderType,
    });
  };

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-3">
      {/* 모바일에서는 모든 항목을 한 줄씩 쌓아 작은 화면과 글자 확대에서도 카드 밖으로 나가지 않게 한다. */}
      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
          쿠폰 유형
          <select
            value={cType}
            onChange={(e) => setCType(e.target.value as CouponType)}
            className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900"
          >
            <option value={COUPON_TYPE.PERCENT}>정률 할인</option>
            <option value={COUPON_TYPE.FIXED}>정액 할인</option>
            <option value={COUPON_TYPE.FREE_DELIVERY}>배달비 무료</option>
          </select>
        </label>
        {cType !== COUPON_TYPE.FREE_DELIVERY ? (
          <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
            할인값
            <input
              type="number"
              value={cValue}
              onChange={(e) => setCValue(Number.parseInt(e.target.value, 10) || 0)}
              className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 tabular-nums"
            />
          </label>
        ) : null}
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600 sm:col-span-2">
          쿠폰 이름
          <input
            type="text"
            value={cName}
            onChange={(e) => setCName(e.target.value)}
            maxLength={30}
            placeholder="쿠폰 이름"
            className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
          유효기간(일)
          <input
            type="number"
            value={cDays}
            min={1}
            onChange={(e) => setCDays(Number.parseInt(e.target.value, 10) || 1)}
            className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 tabular-nums"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
          지급 장수
          <input
            type="number"
            value={cCount}
            min={1}
            onChange={(e) => setCCount(Number.parseInt(e.target.value, 10) || 1)}
            className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 tabular-nums"
          />
        </label>
      </div>
      {/* 사용 조건도 모바일 한 열, 넓은 화면 다중 열로 배치한다. */}
      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
          주문 방식
          <select
            value={cOrderType}
            onChange={(e) => {
              const v = e.target.value;
              setCOrderType(v === "pickup" || v === "delivery" ? v : "");
            }}
            className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900"
          >
            <option value="">전체</option>
            <option value="pickup">포장 전용</option>
            <option value="delivery">배달 전용</option>
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600">
          최소 주문금액(원)
          <input
            type="number"
            value={cMinOrder}
            min={0}
            placeholder="0"
            onChange={(e) => setCMinOrder(Number.parseInt(e.target.value, 10) || 0)}
            className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 tabular-nums"
          />
        </label>
        {cType === COUPON_TYPE.PERCENT ? (
          <label className="flex min-w-0 flex-col gap-1 text-xs text-ink-600 sm:col-span-2">
            최대 할인금액(원)
            <input
              type="number"
              value={cMaxDiscount}
              min={0}
              placeholder="0"
              onChange={(e) => setCMaxDiscount(Number.parseInt(e.target.value, 10) || 0)}
              className="h-9 w-full min-w-0 max-w-full rounded-md border border-ink-300 px-2 text-sm text-ink-900 tabular-nums"
            />
          </label>
        ) : null}
      </div>
      {/* 3줄 - '지급' 버튼을 사용 조건 입력칸 하단으로 이동(개선 4). */}
      <div className="flex justify-end">
        <button
          type="button"
          disabled={pending || !cName.trim()}
          onClick={submit}
          className="h-9 rounded-md bg-brand-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
      <p className="text-[11px] text-ink-500">
        최소 주문금액/최대 할인금액은 비워두면(0) 제한이 없습니다. 최대 할인금액은 정률 할인에만
        적용됩니다.
      </p>
    </div>
  );
}

// 전체 회원 일괄 쿠폰 지급 모달(개선 3).
function BulkCouponModal({ onClose }: { onClose: () => void }) {
  const confirm = useConfirm();
  const [msg, setMsg] = useState<string | null>(null);
  const bulkMut = useMutation({
    mutationFn: (spec: AdminGrantCouponInput) => grantCouponToAll(spec),
    onSuccess: (res) => {
      setMsg(`전체 ${res.users}명에게 쿠폰 ${res.granted}장을 지급했습니다.`);
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "일괄 지급 실패"),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-coupon-title"
        className="flex max-h-[90vh] w-full max-w-lg flex-col gap-3 overflow-y-auto rounded-lg bg-surface p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bulk-coupon-title" className="text-lg font-bold text-ink-900">
          전체 회원 쿠폰 지급
        </h2>
        <p className="text-xs text-ink-600">
          현재 가입된 모든 회원에게 동일한 쿠폰을 발급합니다. 발급 후에는 회수할 수 없으니 내용을
          확인해 주세요.
        </p>

        <CouponGrantForm
          pending={bulkMut.isPending}
          submitLabel={bulkMut.isPending ? "지급 중..." : "전체 지급"}
          onSubmit={async (spec) => {
            const ok = await confirm({
              title: "전체 회원 쿠폰 지급",
              description: `모든 회원에게 "${spec.name}" 쿠폰을 ${spec.count}장씩 지급할까요?`,
              confirmLabel: "지급",
              tone: "brand",
            });
            if (ok) {
              setMsg(null);
              bulkMut.mutate(spec);
            }
          }}
        />

        {msg ? <p className="text-xs text-ink-700">{msg}</p> : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-touch items-center justify-center rounded border border-ink-300 bg-surface px-4 text-sm font-semibold text-ink-900 hover:bg-ink-50"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// 회원 주문 내역 섹션(개선 5). 행 클릭 시 상세 모달.
function MemberOrdersSection({ id }: { id: string }) {
  const ordersQuery = useQuery({
    queryKey: ["admin-member-orders", id],
    queryFn: () => listMemberOrders(id),
  });
  const [detail, setDetail] = useState<AdminOrderDto | null>(null);

  return (
    <fieldset className="rounded-md border border-ink-200 p-3">
      <legend className="px-1 text-xs font-semibold text-ink-600">주문 내역</legend>
      {ordersQuery.isLoading ? (
        <p className="text-sm text-ink-600">불러오는 중...</p>
      ) : (ordersQuery.data?.length ?? 0) === 0 ? (
        <p className="py-4 text-center text-sm text-ink-600">주문 내역이 없습니다.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {ordersQuery.data?.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => setDetail(o)}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-ink-200 bg-white p-2.5 text-left hover:bg-ink-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ink-600 tabular-nums">{o.orderNo}</span>
                    <OrderStatusChip status={o.status} />
                    <PaymentStatusChip status={o.paymentStatus} />
                  </div>
                  <p className="mt-0.5 truncate text-xs text-ink-600">
                    {formatOrderDate(o.createdAt)} · {o.itemCount}종
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums">
                  {o.totalKrw.toLocaleString()}원
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {detail ? <OrderDetailModal order={detail} onClose={() => setDetail(null)} /> : null}
    </fieldset>
  );
}

// 회원 주문 상세 모달(개선 5).
function OrderDetailModal({ order, onClose }: { order: AdminOrderDto; onClose: () => void }) {
  const units = useUnits();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-detail-title"
        className="flex max-h-[90vh] w-full max-w-md flex-col gap-3 overflow-y-auto rounded-lg bg-surface p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 id="order-detail-title" className="text-lg font-bold text-ink-900">
            주문 상세
          </h2>
          <OrderStatusChip status={order.status} />
        </div>

        <dl className="flex flex-col gap-1 rounded-md border border-ink-200 bg-ink-50 p-3 text-sm">
          <ModalRow label="주문번호" value={order.orderNo} />
          <ModalRow label="주문일시" value={formatOrderDateTime(order.createdAt)} />
          <ModalRow label="주문방식" value={order.type === "delivery" ? "배달" : "포장"} />
          <ModalRow
            label="결제"
            value={`${PAYMENT_METHOD_TYPE_LABEL_KO[order.paymentMethodType]} · ${PAYMENT_STATUS_LABEL_KO[order.paymentStatus]}`}
          />
          <ModalRow
            label="수령"
            value={formatDesiredPickup(order.desiredDate, order.desiredTime)}
          />
          {order.delivery ? (
            <ModalRow
              label="배송지"
              value={`${order.delivery.addressLine1}${order.delivery.addressLine2 ? ` ${order.delivery.addressLine2}` : ""}`}
            />
          ) : null}
          {order.customerMemo ? <ModalRow label="요청사항" value={order.customerMemo} /> : null}
          {order.rejectReason ? <ModalRow label="거절 사유" value={order.rejectReason} /> : null}
        </dl>

        {/* 주문 품목 */}
        <ul className="flex flex-col gap-1 rounded-md bg-ink-50 p-3 text-sm">
          {order.items.map((it) => (
            <li key={it.id} className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 flex-1 truncate">
                {it.name}{" "}
                <span className="text-ink-600">
                  ({formatCartLineLabel(units, it.unit, it.quantity)})
                </span>
              </span>
              <span className="shrink-0 tabular-nums">{it.lineTotalKrw.toLocaleString()}원</span>
            </li>
          ))}
          <li className="mt-1 flex items-baseline justify-between border-t border-ink-200 pt-1 text-ink-700">
            <span>상품 합계</span>
            <span className="tabular-nums">{order.subtotalKrw.toLocaleString()}원</span>
          </li>
          {order.appliedCoupons.length > 0 ? (
            <li className="flex items-baseline justify-between text-brand-700">
              <span>쿠폰 {order.appliedCoupons.map((c) => c.name).join(", ")}</span>
              <span className="tabular-nums">
                {order.discountKrw > 0 ? `-${order.discountKrw.toLocaleString()}원` : "배달비 무료"}
              </span>
            </li>
          ) : null}
          {order.complimentDiscountKrw > 0 ? (
            <li className="flex items-baseline justify-between text-brand-700">
              <span>칭찬 감사 할인{order.complimentText ? ` (${order.complimentText})` : ""}</span>
              <span className="tabular-nums">
                -{order.complimentDiscountKrw.toLocaleString()}원
              </span>
            </li>
          ) : null}
          {order.pointsUsedKrw > 0 ? (
            <li className="flex items-baseline justify-between text-brand-700">
              <span>적립금 사용</span>
              <span className="tabular-nums">-{order.pointsUsedKrw.toLocaleString()}원</span>
            </li>
          ) : null}
          {order.type === "delivery" ? (
            <li className="flex items-baseline justify-between text-ink-700">
              <span>배달비</span>
              <span className="tabular-nums">{order.deliveryFeeKrw.toLocaleString()}원</span>
            </li>
          ) : null}
          <li className="mt-1 flex items-baseline justify-between border-t border-ink-200 pt-1 font-semibold">
            <span>합계 (현장 결제)</span>
            <span className="tabular-nums">{order.totalKrw.toLocaleString()}원</span>
          </li>
        </ul>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-touch items-center justify-center rounded border border-ink-300 bg-surface px-4 text-sm font-semibold text-ink-900 hover:bg-ink-50"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// 주문 상태 칩 (관리자 라벨).
function OrderStatusChip({ status }: { status: OrderStatus }) {
  const isCanceled = status === "rejected" || status === "canceled";
  const isDone = status === "completed";
  const className = isCanceled
    ? "bg-ink-200 text-ink-600"
    : isDone
      ? "bg-success-50 text-success-700"
      : "bg-brand-600 text-white";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}>
      {ORDER_STATUS_ADMIN_LABEL_KO[status]}
    </span>
  );
}

// 결제 상태를 주문 상태와 별도로 표시해 완료 주문의 환불 여부까지 목록에서 바로 확인한다.
function PaymentStatusChip({ status }: { status: PaymentStatus }) {
  const className =
    status === PAYMENT_STATUS.REFUNDED || status === PAYMENT_STATUS.REFUND_FAILED
      ? "bg-danger-50 text-danger-700"
      : status === PAYMENT_STATUS.PAID
        ? "bg-success-50 text-success-700"
        : status === PAYMENT_STATUS.FAILED
          ? "bg-danger-600 text-white"
          : status === PAYMENT_STATUS.CHARGING
            ? "bg-warning-50 text-warning-700"
            : "bg-ink-100 text-ink-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}>
      {PAYMENT_STATUS_LABEL_KO[status]}
    </span>
  );
}

function ModalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-16 shrink-0 text-ink-600">{label}</dt>
      <dd className="min-w-0 flex-1 text-ink-800">{value}</dd>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-ink-600">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function formatPhone(p: string): string {
  if (p.length === 11) return `${p.slice(0, 3)}-${p.slice(3, 7)}-${p.slice(7)}`;
  if (p.length === 10) return `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`;
  return p;
}

// 'YYYY-MM-DD HH:MM' (KST) 형태로 주문일시 표시. ISO(UTC)를 KST 로 변환.
function formatOrderDateTime(iso: string): string {
  const d = new Date(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = kst.getUTCFullYear();
  const mm = String(kst.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(kst.getUTCDate()).padStart(2, "0");
  const hh = String(kst.getUTCHours()).padStart(2, "0");
  const mi = String(kst.getUTCMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

// 'M/D' 짧은 날짜 (리스트용).
function formatOrderDate(iso: string): string {
  const d = new Date(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return `${kst.getUTCMonth() + 1}/${kst.getUTCDate()}`;
}
